import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

// Composants
import AppHeader from './components/AppHeader';
import AppNavigation from './components/AppNavigation';
import Dashboard from './components/dashboard/Dashboard';
import BoitierDetail from './components/boitier/BoitierDetail';
import RedirectionAncienneRoute from './components/boitier/RedirectionAncienneRoute';
import AlertsView from './components/alerts/AlertsView';
import SettingsView from './components/settings/SettingsView';
import RegulationView from './components/settings/RegulationView';
import DeviceManagement from './components/admin/DeviceManagement';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LEDCodesPage from './pages/LEDCodesPage';

// Services
import { apiService } from './services/ApiService';

// Context provider
import { SondeProvider } from './context/SondeContext';

// Thème personnalisé
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.2rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
  },
});

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(apiService.isAuthenticated());

  useEffect(() => {
    // Écouter les changements d'authentification (login, logout, 401)
    apiService.setOnAuthChange((authenticated: boolean) => {
      setIsAuthenticated(authenticated);
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <SondeProvider>
                <Box sx={{ display: 'flex', height: '100vh' }}>
                  <AppHeader />
                  <AppNavigation />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      p: 3,
                      mt: 8,
                      overflow: 'auto',
                      backgroundColor: (theme) => theme.palette.background.default,
                    }}
                  >
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/boitier/:id" element={<BoitierDetail />} />
                      {/* Anciennes URL par famille — redirigees vers /boitier/:id */}
                      <Route path="/bain/:id" element={<RedirectionAncienneRoute />} />
                      <Route path="/alerts" element={<AlertsView />} />
                      <Route path="/settings" element={<SettingsView />} />
                      <Route path="/regulation" element={<RegulationView />} />
                      <Route path="/niveau/:id" element={<RedirectionAncienneRoute prefixe="niveau" />} />
                      <Route path="/rincage/:id" element={<RedirectionAncienneRoute prefixe="rincage" />} />
                      <Route path="/debit_pression/:id" element={<RedirectionAncienneRoute prefixe="debit_pression" />} />
                      <Route path="/admin/devices" element={<DeviceManagement />} />
                      <Route path="/admin/devices/new" element={<DeviceManagement />} />
                      <Route path="/codes-led" element={<LEDCodesPage />} />
                    </Routes>
                  </Box>
                </Box>
              </SondeProvider>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
